import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import CommonService from "../../services/common.service";
import { API_URLS } from "../../constants/api-urls";
import "./PurchaseCredits.scss";

export default function PurchaseCredits() {
  const navigate = useNavigate();
  const [reservePrice, setReservePrice] = useState("");
  const [selectedTier, setSelectedTier] = useState("Regular");
  const [loading, setLoading] = useState(false);
  const [pricingStructure, setPricingStructure] = useState([]);
  const [loadingPricing, setLoadingPricing] = useState(true);

  // Fetch pricing structure from database
  useEffect(() => {
    const fetchPricingStructure = async () => {
      try {
        setLoadingPricing(true);
        const response = await CommonService.get(API_URLS.AUCTION.PRICING);
        if (response && response.success && response.data) {
          setPricingStructure(response.data);
        }
      } catch (error) {
        console.error("Failed to fetch pricing structure:", error);
        toast.error("Failed to load pricing structure");
      } finally {
        setLoadingPricing(false);
      }
    };

    fetchPricingStructure();
  }, []);

  // Get pricing tier for a given price
  const getPricingTier = (price) => {
    const amount = parseFloat(price) || 0;
    if (pricingStructure.length === 0) return null;
    
    // Find the pricing tier that matches the price range
    return pricingStructure.find((tier) => {
      const minValue = parseFloat(tier.min_value) || 0;
      const maxValue = tier.max_value ? parseFloat(tier.max_value) : Infinity;
      return amount >= minValue && amount <= maxValue;
    });
  };

  // Reset tier to Regular if reserve price doesn't support Featured
  useEffect(() => {
    if (pricingStructure.length === 0) return;
    
    const price = parseFloat(reservePrice) || 0;
    if (price > 0 && selectedTier === "Featured") {
      const tier = getPricingTier(price);
      if (tier && (!tier.featured_auction_fee || tier.featured_auction_fee === null)) {
        setSelectedTier("Regular");
      }
    }
  }, [reservePrice, selectedTier, pricingStructure]);

  const calculateCreditCost = (price, tier) => {
    const pricingTier = getPricingTier(price);
    if (!pricingTier) return 0;
    
    if (tier === "Featured") {
      return pricingTier.featured_auction_fee || 0;
    }
    return pricingTier.regular_auction_fee || 0;
  };

  const calculateCommission = (price, tier) => {
    const pricingTier = getPricingTier(price);
    if (!pricingTier) return 0;
    
    const commissionRate = tier === "Featured" 
      ? (pricingTier.featured_commission_rate || 0) / 100
      : (pricingTier.regular_commission_rate || 0) / 100;
    
    return (parseFloat(price) || 0) * commissionRate;
  };

  const getCommissionRate = (price, tier) => {
    const pricingTier = getPricingTier(price);
    if (!pricingTier) return "0%";
    
    const rate = tier === "Featured" 
      ? pricingTier.featured_commission_rate || 0
      : pricingTier.regular_commission_rate || 0;
    
    return `${rate}%`;
  };

  const isFeaturedAvailable = (price) => {
    const pricingTier = getPricingTier(price);
    return pricingTier && pricingTier.featured_auction_fee !== null && pricingTier.featured_auction_fee !== undefined;
  };

  // Calculate values separately for each card
  const regularCreditCost = calculateCreditCost(reservePrice, "Regular");
  const regularCommissionEstimate = calculateCommission(reservePrice, "Regular");
  const featuredCreditCost = calculateCreditCost(reservePrice, "Featured");
  const featuredCommissionEstimate = calculateCommission(reservePrice, "Featured");

  const handlePurchase = async () => {
    if (!reservePrice || parseFloat(reservePrice) <= 0) {
      toast.error("Please enter a valid reserve price");
      return;
    }

    if (selectedTier === "Featured" && !isFeaturedAvailable(reservePrice)) {
      toast.error("Featured auctions are not available for this reserve price range");
      return;
    }

    try {
      setLoading(true);
      const response = await CommonService.post(API_URLS.AUCTION.CREDITS, {
        reserve_price: parseFloat(reservePrice),
        tier: selectedTier,
      });

      if (response && response.success) {
        toast.success("Credit purchase initiated successfully!");
        // Redirect to success page with purchase data
        setTimeout(() => {
          navigate("/auction/purchase-credits/success", {
            state: {
              reservePrice: parseFloat(reservePrice),
              tier: selectedTier,
              creditCost: selectedTier === "Regular" ? regularCreditCost : featuredCreditCost,
              transactionId: response.data?.transaction_id || response.data?.transactionId || `TXN-${Date.now()}`,
              creditsAvailable: response.data?.credits_available || response.data?.creditsAvailable || 1,
            },
          });
        }, 1500);
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message || "Failed to purchase credit";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div className="purchase-credits-page">
      <div className="container">
        <div className="page-header">
          <h1>Purchase Auction Credits</h1>
          <p>
            Choose your auction plan based on reserve price. All plans include
            secure bidding, payment facilitation, and 72-hour SLA support.
          </p>
        </div>

        <div className="reserve-price-input">
          <h2>Enter Your Product's Reserve Price</h2>
          <div className="input-group">
            <span className="currency-symbol">₹</span>
            <input
              type="number"
              value={reservePrice}
              onChange={(e) => setReservePrice(e.target.value)}
              placeholder="0"
              min="0"
            />
          </div>
          <p className="info-text">
            <i className="info-icon">ℹ</i> Your reserve price determines the
            auction fee and commission structure
          </p>
        </div>

        {/* Pricing Cards - Only show when reserve price is entered */}
        {reservePrice && parseFloat(reservePrice) > 0 && (
          <div className="pricing-cards">
            <div
              className={`pricing-card ${selectedTier === "Regular" ? "selected" : ""}`}
              onClick={() => setSelectedTier("Regular")}
            >
              <div className="card-header">
                <div>
                  <h3>Regular Auction</h3>
                  <p>Standard visibility & features</p>
                </div>
                {selectedTier === "Regular" && (
                  <div className="check-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 6 9 17l-5-5"></path>
                    </svg>
                  </div>
                )}
              </div>
              <div className="card-price-section">
                <div className="price-wrapper">
                  <span className="price">{formatCurrency(regularCreditCost)}</span>
                  <span className="period">/ auction</span>
                </div>
                <div className="commission-badge">
                  + {getCommissionRate(reservePrice, "Regular")} commission on final sale
                </div>
              </div>
              <ul className="features">
                <li>✓ One Product Auction</li>
                <li>✓ 30 Days Active</li>
                <li>✓ Standard Listing</li>
                <li>✗ Premium Visibility</li>
                <li>✗ Featured Badge</li>
              </ul>
              <div className="commission-estimate">
                <div className="estimate-header">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="alert-icon">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" x2="12" y1="8" y2="12"></line>
                    <line x1="12" x2="12.01" y1="16" y2="16"></line>
                  </svg>
                  <p className="estimate-title">Commission Estimate</p>
                </div>
                <p className="estimate-amount">Approx. {formatCurrency(regularCommissionEstimate)} based on your reserve price</p>
              </div>
            </div>

            {isFeaturedAvailable(reservePrice) && (
              <div
                className={`pricing-card ${selectedTier === "Featured" ? "selected" : ""}`}
                onClick={() => setSelectedTier("Featured")}
              >
                <div className="recommended-badge">RECOMMENDED</div>
                <div className="card-header">
                  <div>
                    <h3>Featured Auction</h3>
                    <p>Maximum visibility & priority</p>
                  </div>
                  {selectedTier === "Featured" && (
                    <div className="check-icon">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20 6 9 17l-5-5"></path>
                      </svg>
                    </div>
                  )}
                </div>
                <div className="card-price-section">
                  <div className="price-wrapper">
                    <span className="price">{formatCurrency(featuredCreditCost)}</span>
                    <span className="period">/ auction</span>
                  </div>
                  <div className="commission-badge">
                    + {getCommissionRate(reservePrice, "Featured")} commission on final sale
                  </div>
                </div>
                <ul className="features">
                  <li>✓ One Product Auction</li>
                  <li>✓ 30 Days Active</li>
                  <li>✓ Top Search Results</li>
                  <li>✓ Featured Badge</li>
                  <li>✓ Homepage Featured</li>
                </ul>
                <div className="commission-estimate">
                  <div className="estimate-header">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="alert-icon">
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="12" x2="12" y1="8" y2="12"></line>
                      <line x1="12" x2="12.01" y1="16" y2="16"></line>
                    </svg>
                    <p className="estimate-title">Commission Estimate</p>
                  </div>
                  <p className="estimate-amount">Approx. {formatCurrency(featuredCommissionEstimate)} based on your reserve price</p>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="pricing-table">
          <h2>Complete Pricing Structure</h2>
          {loadingPricing ? (
            <div className="loading-message">Loading pricing structure...</div>
          ) : pricingStructure.length === 0 ? (
            <div className="error-message">Failed to load pricing structure</div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th rowSpan="2">Auction Value Range (INR)</th>
                  <th colSpan="3">Non Featured Auctions</th>
                  <th colSpan="3">Featured Auctions</th>
                  <th rowSpan="2">Buyer Fee (Scrap Category)</th>
                  <th rowSpan="2">Buyer Fee (Other Categories)</th>
                </tr>
                <tr>
                  <th>Auction Fees</th>
                  <th>Commission</th>
                  <th>Auction Valid Days</th>
                  <th>Auction Fees</th>
                  <th>Featured auctions Commission</th>
                  <th>Auction Valid Days</th>
                </tr>
              </thead>
              <tbody>
                {pricingStructure.map((tier, index) => (
                  <tr key={tier.pricing_id || index}>
                    <td>{tier.value_range_label}</td>
                    <td>{formatCurrency(tier.regular_auction_fee)}</td>
                    <td>{tier.regular_commission_rate}%</td>
                    <td>{tier.regular_valid_days} Days</td>
                    <td>
                      {tier.featured_auction_fee !== null && tier.featured_auction_fee !== undefined
                        ? formatCurrency(tier.featured_auction_fee)
                        : "NA"}
                    </td>
                    <td>{tier.featured_commission_rate}%</td>
                    <td>{tier.featured_valid_days} Days</td>
                    <td>{tier.buyer_fee_scrap_category}%</td>
                    <td>
                      {tier.buyer_fee_other_categories === 0 || tier.buyer_fee_other_categories === null
                        ? "None"
                        : `${tier.buyer_fee_other_categories}%`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Action Buttons - Only show when reserve price is entered and tier is selected */}
        {reservePrice && parseFloat(reservePrice) > 0 && (
          <div className="action-buttons">
            <button className="btn-cancel" onClick={() => navigate(-1)}>
              Cancel
            </button>
            <button
              className="btn-primary"
              onClick={handlePurchase}
              disabled={loading || !reservePrice}
            >
              {loading ? "Processing..." : `Purchase ${selectedTier} Auction Credit`}
            </button>
          </div>
        )}

        <div className="important-notice">
          <div className="notice-icon">⚠</div>
          <div className="notice-content">
            <h3>Important Notice</h3>
            <ul>
              <li>Auction credits are valid for 30 days from purchase date</li>
              <li>Each credit allows publication of one auction listing</li>
              <li>Commission is calculated on the final sale price (if auction is successful)</li>
              <li>
                Trocanet acts as a neutral facilitator - you retain ownership until buyer acceptance
              </li>
              <li>
                All transactions are subject to 72-hour SLA timers at key workflow stages
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
